import { msJsSSRClient } from '@/apiClients';
import { CookiesKeys, DeliveryStyleEnum, mixedCartErrorMessage } from '@/constants';
import { ServerActionError } from '@/utils/action-utils';
import type {
  PGCandidateOrderEntity,
  PGCorpCandidateOrderPayload,
  PGCorpDelivery,
  PGCorpItemPayload,
} from '@wolfejs/ms-api-js/src/types/orders';
import Cookies from 'js-cookie';

type DeliveryItemCandidateOrder = PGCorpDelivery & {
  id_key: string;
};

export async function getOrSetCart() {
  const cartId = Cookies.get(CookiesKeys.pgCorpCartUuid);
  let cart;

  if (cartId) {
    cart = await msJsSSRClient.orders.getCandidateOrder(cartId);
  }

  return cart?.data;
}

const isDigital = (deliveries: PGCorpDelivery[]) => deliveries.some(del => del.style === DeliveryStyleEnum.SPREADSHEET);

export const addToCart = async ({ pgCorpPayload }: { pgCorpPayload: PGCorpCandidateOrderPayload }) => {
  console.info('Front end version of addToCart called. Deleteme after testing');
  let pgCorpPayloadToSend = pgCorpPayload;
  const cartId = Cookies.get(CookiesKeys.pgCorpCartUuid);
  // if there is already a cartId, check if the cart has items and if so we are updateing the
  // candidate order.  If not, it is a new candidate order.
  if (cartId && cartId !== 'undefined') {
    const existingCart = await msJsSSRClient.orders.getCandidateOrder(cartId);

    if (existingCart) {
      const existingCartData = existingCart.data as PGCandidateOrderEntity;

      // check if new items are not same type as existing
      const newIsDigital = isDigital(pgCorpPayload.deliveries);
      const existingIsDigital = isDigital(existingCartData.deliveries);
      if (newIsDigital !== existingIsDigital) {
        throw new Error(mixedCartErrorMessage);
      }

      // add new payload to existing payload.... anytime we update a candidate order, it
      // needs the full data, not just what you are updating.
      // Need to start with existing cart, map through it and massage the data into the
      // formatt needed for both items and deliveries that pgcorp candidateOrder needs. Then
      // finally add the new order items to the pgCorpPayloadToSend.

      const allItems = existingCartData.items;
      const allDeliveries = existingCartData.deliveries as DeliveryItemCandidateOrder[];

      let updatedItemsList: PGCorpItemPayload[] = [];
      let updatedDeliveriesList: PGCorpDelivery[] = [];
      const deliveryIdsUsed: string[] = [];

      // reformat existing items into format needed to send to pgcorp
      allItems.map(itm => {
        const newItem: PGCorpItemPayload = {
          product: itm.product,
          denomination: itm.denomination,
          delivery: itm.delivery_key!,
          attributes: itm.attributes,
        };
        updatedItemsList.push(newItem);
        // keep track of used delivery objects so we know if we have to add any.
        if (itm.delivery_key && !deliveryIdsUsed.includes(itm.delivery_key)) {
          deliveryIdsUsed.push(itm.delivery_key);
        }
      });

      // reformat existing deliveries into format needed to send to pgcorp
      allDeliveries.map(del => {
        const newDelivery: PGCorpDelivery = {
          ...del,
          id: del.id_key,
        };
        updatedDeliveriesList.push(newDelivery);
      });

      // Check if the new item to add needs a new delivery item
      let needsDeliveryAdded = false;
      pgCorpPayload.deliveries.map(del => {
        if (!deliveryIdsUsed.includes(del.id)) {
          needsDeliveryAdded = true;
        }
      });

      // Add the new items and deliveries (if needed) to the existing items
      updatedItemsList = [...updatedItemsList, ...pgCorpPayload.items];
      if (needsDeliveryAdded) {
        updatedDeliveriesList = [...updatedDeliveriesList, ...pgCorpPayload.deliveries];
      }

      pgCorpPayloadToSend = {
        items: updatedItemsList,
        deliveries: updatedDeliveriesList,
        'payment-method': pgCorpPayload['payment-method'],
        uuid: cartId,
      };
    }
  }
  const cart = await msJsSSRClient.orders.candidateOrder(pgCorpPayloadToSend);
  const candidateOrderUuid = cart.data.uuid;
  if (!cart) {
    throw new ServerActionError('Failed to create candidate order');
  }
  if (candidateOrderUuid) Cookies.set(CookiesKeys.pgCorpCartUuid, candidateOrderUuid);

  return cart.data;
};

export async function deleteLineItem(productId: string, quantity: number, denomination: number, fundsBankUuid: string) {
  console.info('Front end version of deleteLineItem called. Deleteme after testing');
  const cartId = Cookies.get(CookiesKeys.pgCorpCartUuid);

  if (!cartId) {
    throw new Error('Missing cart ID');
  }

  if (!productId || !quantity || !denomination) {
    throw new Error('Missing lineItem');
  }

  const existingCart = await msJsSSRClient.orders.getCandidateOrder(cartId);
  const pgCorpPayload = existingCart.data as PGCandidateOrderEntity;

  const allItems = pgCorpPayload.items;
  const updatedItemsList: PGCorpItemPayload[] = [];
  const deliveryIdsUsed: string[] = [];

  let removedCount = 0;
  allItems.map(itm => {
    if (removedCount < quantity && itm.product === productId && itm.denomination === denomination) {
      // skip this one but increment removed Count
      removedCount++;
    } else {
      const newItem: PGCorpItemPayload = {
        product: itm.product,
        denomination: itm.denomination,
        delivery: itm.delivery_key!, // this has to be here - or couldnt of created candidateOrder to start
        attributes: itm.attributes,
      };
      updatedItemsList.push(newItem);
      // keep track of used delivery objects so we can remove ones that are no longer used.
      if (itm.delivery_key && !deliveryIdsUsed.includes(itm.delivery_key)) {
        deliveryIdsUsed.push(itm.delivery_key);
      }
    }
  });

  // remove any no longer used deliveries array items:
  const deliveriesArray = pgCorpPayload.deliveries as DeliveryItemCandidateOrder[];
  const newDeliveries: PGCorpDelivery[] = [];
  deliveriesArray.map(del => {
    if (deliveryIdsUsed.includes(del.id_key)) {
      const index = newDeliveries.find(itm => itm.id === del.id_key);
      if (!index) {
        const newItm = {
          ...del,
          id: del.id_key,
        };
        newDeliveries.push(newItm);
      }
    }
  });

  const newPayload = {
    uuid: cartId,
    items: updatedItemsList,
    deliveries: newDeliveries,
    'payment-method': fundsBankUuid,
  };

  const cart = await msJsSSRClient.orders.candidateOrder(newPayload);
  const candidateOrderUuid = cart.data?.uuid;
  if (!cart) {
    throw new ServerActionError('Failed to create candidate order');
  }
  if (cart && candidateOrderUuid) {
    Cookies.set(CookiesKeys.pgCorpCartUuid, candidateOrderUuid);
  } else {
    // emptied cart, so no uuid will be returned and we should delete cookie
    Cookies.remove(CookiesKeys.pgCorpCartUuid);
  }

  return cart.data;
}
